import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLang } from '../context/LangContext';
import { getServiceBySlug } from '../data/servicesData';

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const navigate = useNavigate();

  const rawService = getServiceBySlug(slug);

  // Force scroll to top when switching service detail pages
  useEffect(() => {
    if (!window.location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [slug]);

  // Track Ad PageView event on mount
  useEffect(() => {
    if (rawService) {
      // Meta Pixel PageView tracking placeholder
      if (window.fbq) {
        window.fbq('track', 'PageView');
        window.fbq('trackCustom', 'ViewServiceDetail', { service: slug });
      }
      // Google Analytics / Google Ads event tracking placeholder
      if (window.gtag) {
        window.gtag('event', 'page_view', {
          page_title: rawService[lang]?.title || slug,
          page_location: window.location.href,
          page_path: window.location.pathname,
        });
      }
    }
  }, [slug, lang, rawService]);

  if (!rawService) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 py-16">
        <h1 className="text-3xl font-bold mb-4 text-gray-900">
          {lang === 'cn' ? '未找到相关服务' : 'Service Not Found'}
        </h1>
        <p className="text-gray-600 mb-8">
          {lang === 'cn' ? '抱歉，您访问的服务页面不存在或已下架。' : 'Sorry, the service page you requested does not exist.'}
        </p>
        <Link
          to="/services"
          className="px-6 py-3 rounded-xl bg-[#2E6B4C] text-white hover:bg-[#23533a] transition-all shadow-md"
        >
          {lang === 'cn' ? '返回服务列表' : 'Back to All Services'}
        </Link>
      </div>
    );
  }

  const s = rawService[lang] || rawService.en;
  const canonicalUrl = `https://www.haloaestheticclinic.com/services/${slug}`;

  // JSON-LD MedicalProcedure & FAQ Schema
  const schemaJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: s.title,
    description: s.description || s.purpose,
    image: rawService.image,
    url: canonicalUrl,
    procedureType: 'https://schema.org/NonInvasiveProcedure',
    howItWorks: s.mechanism,
    bodyLocation: s.indications ? s.indications.join(', ') : 'Skin & Facial areas',
    relevantSpecialty: {
      '@type': 'MedicalSpecialty',
      name: 'Dermatology & Medical Aesthetics',
    },
  };

  const faqSchemaJsonLd = s.faq && s.faq.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: s.faq.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  } : null;

  const handleBookClick = () => {
    if (window.fbq) {
      window.fbq('track', 'Lead', { service: slug });
    }
    if (window.gtag) {
      window.gtag('event', 'conversion', { send_to: 'AW-CONVERSION-ID/BOOKING' });
    }
    navigate('/book-form');
  };

  return (
    <>
      <Helmet>
        <title>{s.meta?.title || `${s.title} | Halo Aesthetic Clinic`}</title>
        <meta name="description" content={s.meta?.description || s.description} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={s.meta?.title || s.title} />
        <meta property="og:description" content={s.meta?.description || s.description} />
        <meta property="og:image" content={rawService.image} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />

        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(schemaJsonLd)}</script>
        {faqSchemaJsonLd && (
          <script type="application/ld+json">{JSON.stringify(faqSchemaJsonLd)}</script>
        )}
      </Helmet>

      <div className="bg-softGradient bg-no-repeat bg-top text-text pb-24">
        {/* Breadcrumb & Top Bar */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2">
          <nav className="flex text-sm text-gray-500 space-x-2">
            <Link to="/" className="hover:text-gray-900 transition-colors">
              {lang === 'cn' ? '首页' : 'Home'}
            </Link>
            <span>/</span>
            <Link to="/services" className="hover:text-gray-900 transition-colors">
              {lang === 'cn' ? '服务项目' : 'Services'}
            </Link>
            <span>/</span>
            <span className="text-[#2E6B4C] font-medium">{s.title}</span>
          </nav>
        </div>

        {/* Hero Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="space-y-6">
              <span className="inline-block px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider bg-champagne/40 text-[#2E6B4C] border border-champagne/60">
                {rawService.category === 'injection' ? (lang === 'cn' ? '注射美容' : 'Injection Aesthetics') :
                 rawService.category === 'skin' ? (lang === 'cn' ? '皮肤护理' : 'Skin Care') :
                 (lang === 'cn' ? '身体塑形' : 'Body Shaping')}
              </span>

              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                {s.title}
              </h1>

              {s.subtitle && (
                <p className="text-lg md:text-xl text-[#2E6B4C] font-medium leading-relaxed">
                  {s.subtitle}
                </p>
              )}

              <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                {s.description}
              </p>

              {/* Call to Actions */}
              <div className="flex flex-wrap gap-4 pt-4">
                <button
                  onClick={handleBookClick}
                  className="px-6 py-3.5 rounded-xl text-gray-900 font-semibold shadow-soft hover:shadow-glow hover:brightness-110 border border-champagne/60 transition-all text-center"
                  style={{
                    backgroundImage:
                      'linear-gradient(90deg, rgba(217,195,169,1) 0%, rgba(217,195,169,0.8) 40%, rgba(217,195,169,0.3) 100%)',
                  }}
                >
                  {lang === 'cn' ? '立即在线预约' : 'Book Consultation Now'}
                </button>

                <a
                  href="tel:942-388-1688"
                  className="px-6 py-3.5 rounded-xl bg-white/80 hover:bg-white text-gray-900 font-semibold border border-sage/60 shadow-sm transition-all flex items-center justify-center space-x-2"
                >
                  <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>942-388-1688</span>
                </a>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-[#2E6B4C]/20 to-transparent rounded-2xl filter blur-xl transform scale-95"></div>
              <img
                src={rawService.image}
                alt={s.title}
                className="relative w-full h-[320px] sm:h-[420px] object-cover rounded-2xl shadow-card border border-champagne/40"
              />
            </div>
          </div>
        </section>

        {/* Indications Section */}
        {s.indications && s.indications.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-champagne/40 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-2.5 h-6 bg-[#2E6B4C] rounded-full mr-3 inline-block"></span>
                {lang === 'cn' ? '适用人群与主要改善问题' : 'Target Concerns & Indications'}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {s.indications.map((item, idx) => (
                  <div key={idx} className="flex items-start p-4 rounded-xl bg-champagne/15 border border-champagne/30">
                    <svg className="w-5 h-5 text-[#2E6B4C] mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-800 font-medium text-sm md:text-base">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Treatment Breakdown */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-champagne/40 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-[#2E6B4C]/10 text-[#2E6B4C] flex items-center justify-center font-bold mb-4">
                01
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{lang === 'cn' ? '治疗目的' : 'Purpose'}</h3>
              <p className="text-gray-700 text-sm leading-relaxed">{s.purpose}</p>
            </div>

            <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-champagne/40 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-[#2E6B4C]/10 text-[#2E6B4C] flex items-center justify-center font-bold mb-4">
                02
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{lang === 'cn' ? '作用原理' : 'Mechanism'}</h3>
              <p className="text-gray-700 text-sm leading-relaxed">{s.mechanism}</p>
            </div>

            <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-champagne/40 shadow-sm">
              <div className="w-10 h-10 rounded-xl bg-[#2E6B4C]/10 text-[#2E6B4C] flex items-center justify-center font-bold mb-4">
                03
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{lang === 'cn' ? '疗程与维持时间' : 'Duration & longevities'}</h3>
              <p className="text-gray-700 text-sm leading-relaxed">{s.duration}</p>
            </div>
          </div>
        </section>

        {/* Sub-services / Custom Variations */}
        {s.subServices && s.subServices.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="bg-white/90 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-champagne/40 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-2.5 h-6 bg-[#2E6B4C] rounded-full mr-3 inline-block"></span>
                {lang === 'cn' ? '包含细分项目与特调配方' : 'Available Sub-Services & Custom Formulations'}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {s.subServices.map((sub, index) => (
                  <div key={index} className="p-5 rounded-xl bg-gray-50/80 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{sub.name}</h3>
                    {sub.ingredients && (
                      <p className="text-xs font-semibold text-[#2E6B4C] mb-3 uppercase tracking-wider">
                        {sub.ingredients}
                      </p>
                    )}
                    <ul className="space-y-2">
                      {sub.benefits.map((b, bIdx) => (
                        <li key={bIdx} className="flex items-start text-sm text-gray-700">
                          <svg className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Treatment Process */}
        {s.process && s.process.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-champagne/40 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-2.5 h-6 bg-[#2E6B4C] rounded-full mr-3 inline-block"></span>
                {lang === 'cn' ? '标准护理流程' : 'Treatment Process'}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {s.process.map((step, idx) => (
                  <div key={idx} className="relative p-5 rounded-xl bg-white border border-champagne/30 shadow-sm">
                    <span className="text-xs font-bold text-[#2E6B4C] uppercase tracking-widest block mb-1">
                      Step {idx + 1}
                    </span>
                    <h3 className="text-base font-bold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-xs text-gray-600 leading-relaxed">{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ Section */}
        {s.faq && s.faq.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-champagne/40 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="w-2.5 h-6 bg-[#2E6B4C] rounded-full mr-3 inline-block"></span>
                {lang === 'cn' ? '常见问题解答' : 'Frequently Asked Questions'}
              </h2>
              <div className="space-y-4">
                {s.faq.map((item, idx) => (
                  <div key={idx} className="p-5 rounded-xl bg-gray-50 border border-gray-200">
                    <h3 className="text-base font-bold text-gray-900 mb-2 flex items-start">
                      <span className="text-[#2E6B4C] mr-2">Q:</span>
                      <span>{item.q}</span>
                    </h3>
                    <p className="text-sm text-gray-700 pl-6 leading-relaxed">
                      {item.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>

      {/* Sticky Bottom Floating CTA Bar (Optimized for Mobile & Ad Conversions) */}
      <div className="fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur-md border-t border-champagne/60 z-40 px-4 py-3 shadow-2xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="hidden sm:flex flex-col">
            <span className="text-sm font-bold text-gray-900">{s.title}</span>
            <span className="text-xs text-gray-600">Richmond Hill Medical Aesthetics</span>
          </div>

          <div className="flex items-center space-x-3 w-full sm:w-auto justify-end">
            <a
              href="tel:942-388-1688"
              className="flex-1 sm:flex-initial px-4 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-900 font-medium text-sm border border-gray-300 transition-all text-center flex items-center justify-center space-x-1.5"
            >
              <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <span>{lang === 'cn' ? '一键电话咨询' : 'Call 942-388-1688'}</span>
            </a>

            <button
              onClick={handleBookClick}
              className="flex-1 sm:flex-initial px-6 py-2.5 rounded-xl text-gray-900 font-semibold text-sm shadow-soft hover:shadow-glow hover:brightness-110 border border-champagne/60 transition-all text-center"
              style={{
                backgroundImage:
                  'linear-gradient(90deg, rgba(217,195,169,1) 0%, rgba(217,195,169,0.8) 40%, rgba(217,195,169,0.3) 100%)',
              }}
            >
              {lang === 'cn' ? '立即预约' : 'Book Now'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceDetailPage;
